/**
 * 🏢 Enterprise Project Types
 *
 * Projects are the universal container of work.
 * Hierarchy: Organization → Projects → Assets
 *
 * Asset types: Process Maps, Workflows, Problems, Actions, Blueprints, Indicators
 */

// ─── Visibility ─────────────────────────────────────────────

/** Visibility scope for all enterprise assets */
export type VisibilityScope = 'private' | 'project' | 'organization';

export const VISIBILITY_LABELS: Record<VisibilityScope, string> = {
  private: 'Privado',
  project: 'Projeto',
  organization: 'Organização',
};

export const VISIBILITY_DESCRIPTIONS: Record<VisibilityScope, string> = {
  private: 'Visível apenas para o criador',
  project: 'Visível para membros do projeto',
  organization: 'Visível para toda a organização',
};

// ─── Project ────────────────────────────────────────────────

export type ProjectStatus = 'draft' | 'active' | 'on_hold' | 'completed' | 'archived';

export const PROJECT_STATUS_LABELS: Record<ProjectStatus, string> = {
  draft: 'Rascunho',
  active: 'Ativo',
  on_hold: 'Pausado',
  completed: 'Concluído',
  archived: 'Arquivado',
};

export const PROJECT_STATUS_COLORS: Record<ProjectStatus, string> = {
  draft: 'bg-slate-500/10 text-slate-600 border border-slate-500/20 dark:text-slate-400',
  active: 'bg-blue-500/10 text-blue-600 border border-blue-500/20 dark:text-blue-400',
  on_hold: 'bg-amber-500/10 text-amber-600 border border-amber-500/20 dark:text-amber-400',
  completed: 'bg-emerald-500/10 text-emerald-600 border border-emerald-500/20 dark:text-emerald-400',
  archived: 'bg-slate-500/10 text-slate-500 border border-slate-500/20 dark:text-slate-500',
};

export interface EnterpriseProject {
  id: string;
  name: string;
  description?: string | null;
  status: ProjectStatus;
  owner_id: string;
  organization_id: string;
  start_date?: string | null;
  target_date?: string | null;
  progress: number; // 0-100 (completion_percentage)
  visibility_scope: VisibilityScope;
  template_id?: string | null;
  created_by?: string | null;
  created_at: string;
  updated_at: string;
}

// ─── Project Members ────────────────────────────────────────

export type ProjectMemberRole = 'owner' | 'editor' | 'viewer';

export const MEMBER_ROLE_LABELS: Record<ProjectMemberRole, string> = {
  owner: 'Proprietário',
  editor: 'Editor',
  viewer: 'Visualizador',
};

export type OperationalRole = 'responsible' | 'executor' | 'support' | 'observer';

export const OPERATIONAL_ROLE_LABELS: Record<OperationalRole, string> = {
  responsible: 'Responsável',
  executor: 'Executor',
  support: 'Apoio',
  observer: 'Observador',
};

export interface ProjectMember {
  id: string;
  project_id: string;
  user_id: string;
  role: ProjectMemberRole;
  operational_role?: OperationalRole | null;
  created_at: string;
  // Joined from user profile
  user_name?: string | null;
  user_email?: string | null;
}

// ─── Project Dashboard Metrics ──────────────────────────────

export interface ProjectDashboardMetrics {
  project_id: string;
  organization_id: string;
  name: string;
  status: ProjectStatus;
  progress: number;
  owner_id: string;
  start_date?: string | null;
  target_date?: string | null;
  open_actions: number;
  open_problems: number;
  active_workflows: number;
  active_indicators: number;
  blueprints_count: number;
  members_count: number;
}

// ─── Asset Visibility Mixin ─────────────────────────────────

/** Fields every enterprise asset must have for visibility */
export interface AssetVisibility {
  visibility_scope: VisibilityScope;
  owner_id?: string | null;
  project_id?: string | null;
  organization_id: string;
}

// ─── Asset Type (for project dashboard context) ─────────────

export type AssetType =
  | 'process_map'
  | 'workflow'
  | 'problem'
  | 'action'
  | 'blueprint'
  | 'indicator';

export const ASSET_TYPE_LABELS: Record<AssetType, string> = {
  process_map: 'Mapa de Processo',
  workflow: 'Workflow',
  problem: 'Problema',
  action: 'Ação',
  blueprint: 'Padrão',
  indicator: 'Indicador',
};

// ─── Project Health ──────────────────────────────────────────

export type ProjectHealth = 'healthy' | 'warning' | 'critical';

export const PROJECT_HEALTH_LABELS: Record<ProjectHealth, string> = {
  healthy: 'Saudável',
  warning: 'Em Risco',
  critical: 'Crítico',
};

export const PROJECT_HEALTH_COLORS: Record<ProjectHealth, string> = {
  healthy: 'bg-emerald-500/10 text-emerald-600 border border-emerald-500/20 dark:text-emerald-400',
  warning: 'bg-amber-500/10 text-amber-600 border border-amber-500/20 dark:text-amber-400',
  critical: 'bg-red-500/10 text-red-600 border border-red-500/20 dark:text-red-400',
};

export const PROJECT_HEALTH_DOT: Record<ProjectHealth, string> = {
  healthy: 'bg-emerald-500',
  warning: 'bg-amber-500',
  critical: 'bg-red-500',
};

// ─── Project Attachment ──────────────────────────────────────

export interface ProjectAttachment {
  id: string;
  project_id: string;
  organization_id: string;
  name: string;
  file_url: string;
  file_path: string;
  file_type: string;
  file_size?: number | null;
  uploaded_by: string;
  created_at: string;
}
